// `z` kommt aus `@hono/zod-openapi` (Ladereihenfolge, F14).
import { z } from '@hono/zod-openapi';
import { IdempotencyKeyHeader } from '../core/headers.js';
import { PageQuery, pageOf } from '../core/pagination.js';
import { type ApiRoute, defineRoute } from '../core/route.js';
import {
  AccountStatus,
  Activity,
  BackgroundOperation,
  CallbackQuery,
  ConnectLinkedIn,
  ConversationListItem,
  DataGapItem,
  DisconnectQuery,
  ErasePersonBody,
  errorResponses,
  FetchRunItem,
  HealthStatus,
  IdentityDecisionBody,
  IdentityReview,
  ListConversationsQuery,
  ListFetchRunsQuery,
  ListMessagesQuery,
  ListPersonsQuery,
  ListPrivacyRequestsQuery,
  LoginQuery,
  Me,
  MergePersonBody,
  MessageItem,
  NetworkSummary,
  NetworkSummaryQuery,
  PersonDetail,
  PersonExport,
  PersonListItem,
  PrepareActionBody,
  PreparedAction,
  PrivacyRequest,
  PrivacyRequestCreate,
  PrivacyRequestPatch,
  ProcessingProgress,
  ReadyStatus,
  RedirectHeaders,
  RefreshAccountBody,
  Reminder,
  RenewTokenBody,
  RestrictPersonBody,
  SessionModeBody,
  Settings,
  SettingsUpdate,
  StrengthFeedbackBody,
  TimelineItem,
  UndoIdentityDecisionBody,
} from './core-schemas.js';

/**
 * Routen des Moduls `core` (TP1, [A] 5.12, Plan D 3.4): Gesundheit, Anmeldung, Sitzung, Netzwerk, Personen,
 * Gespräche, Zuordnungen, Konten, Lücken, Erinnerungen, Vorgänge, Einstellungen, Betroffenenanfragen.
 * Pfade sind absolut (`/api/v1/…`, nur `/healthz` und `/readyz` an der Wurzel). Fehlerantworten folgen Plan D 2.4.
 */

const json = <S extends z.ZodType>(schema: S) => ({ 'application/json': { schema } });
const ok = <S extends z.ZodType>(description: string, schema: S) => ({
  description,
  content: json(schema),
});
const requiredBody = <S extends z.ZodType>(schema: S) => ({
  required: true as const,
  content: json(schema),
});
const optionalBody = <S extends z.ZodType>(schema: S) => ({
  required: false as const,
  content: json(schema),
});

const OperationRef = z.strictObject({ operationId: z.uuid() });

const PersonParam = z.strictObject({ personId: z.uuid() });
const ConversationParam = z.strictObject({ conversationId: z.uuid() });
const LinkParam = z.strictObject({ linkId: z.uuid() });
const IdentityParam = z.strictObject({ identityId: z.uuid() });
const AccountParam = z.strictObject({ accountId: z.uuid() });
const GapParam = z.strictObject({ gapId: z.uuid() });
const ReminderParam = z.strictObject({ reminderId: z.uuid() });
const OperationParam = z.strictObject({ operationId: z.uuid() });
const RequestParam = z.strictObject({ requestId: z.uuid() });

const AuthMeta = {
  module: 'core',
  auth: 'public',
  csrf: false,
  idempotency: 'none',
  demo: 'allow',
} as const;
const ReadMeta = {
  module: 'core',
  auth: 'session',
  csrf: false,
  idempotency: 'none',
  demo: 'allow',
} as const;
const WriteMeta = {
  module: 'core',
  auth: 'session',
  csrf: true,
  idempotency: 'none',
  demo: 'allow',
} as const;
const KeyMeta = { ...WriteMeta, idempotency: 'key' } as const;
const ForbiddenInDemo = { demo: 'read_only_forbidden' } as const;
const Steward = { dataSteward: true } as const;

// ------------------------------------------------------------------------------------------------
// Gesundheit und Anmeldung
// ------------------------------------------------------------------------------------------------

export const healthz = defineRoute(
  { ...AuthMeta, name: 'healthz', base: 'root' },
  {
    method: 'get',
    path: '/healthz',
    summary: 'Lebenszeichen des Prozesses',
    responses: { 200: ok('Der Prozess läuft', HealthStatus), ...errorResponses([400, 500]) },
  },
);

export const readyz = defineRoute(
  { ...AuthMeta, name: 'readyz', base: 'root' },
  {
    method: 'get',
    path: '/readyz',
    summary: 'Bereitschaft (Datenbank, Ablaufsteuerung, Migrationen)',
    responses: { 200: ok('Bereit', ReadyStatus), ...errorResponses([400, 500, 503]) },
  },
);

/**
 * Login und Callback sind Browser-Navigationsrouten: der Browser folgt den Weiterleitungen und zeigt die Antwort an,
 * es gibt keinen Client, der ein Problem-Objekt auswertet. Der Callback antwortet daher bei Fehlern nach der
 * Routenprüfung mit einer HTML-Fehlerseite (`pages.ts`), nicht mit `application/problem+json`.
 */
const LOGIN_NAVIGATION_NOTE =
  'Browser-Navigationsroute: Fehler (429, 500) kommen weiterhin als `application/problem+json`; eine HTML-Fehlerseite gibt es hier nicht (ein ungültiges `returnTo` führt nicht zu 400, sondern zum Rückfall `/`).';
const CALLBACK_NAVIGATION_NOTE =
  'Browser-Navigationsroute: Fehler nach der Routenprüfung liefern eine HTML-Fehlerseite statt `application/problem+json` (400: Anmeldung abgelaufen, abgebrochen oder ungültig; 503: Microsoft nicht erreichbar). Ratengrenze (429) und Validierungsfehler der Routenprüfung bleiben `application/problem+json`.';

export const login = defineRoute(
  { ...AuthMeta, name: 'login' },
  {
    method: 'get',
    path: '/api/v1/auth/login',
    summary: 'Anmeldung bei Microsoft Entra ID starten',
    description:
      'Leitet zu Entra weiter. `returnTo` ist ein interner Pfad (Schema `ReturnTo`); der Handler prüft ihn zusätzlich gegen die konfigurierte Origin und fällt sonst auf `/` zurück. ' +
      LOGIN_NAVIGATION_NOTE,
    request: { query: LoginQuery },
    responses: {
      302: { description: 'Weiterleitung zu Entra', headers: RedirectHeaders },
      ...errorResponses([400, 429, 500]),
    },
  },
);

export const authCallback = defineRoute(
  { ...AuthMeta, name: 'authCallback' },
  {
    method: 'get',
    path: '/api/v1/auth/callback',
    summary: 'Rückkehr von Microsoft Entra ID',
    description:
      'Prüft Zustand, Code und Konto, legt die Sitzung an und leitet weiter (Rücksprungziel oder `/`); abgelehnte Konten landen auf `/kein-zugang`. Zusätzliche Parameter von Entra (`session_state`, `iss`, `client_info`) werden ignoriert. ' +
      CALLBACK_NAVIGATION_NOTE,
    request: { query: CallbackQuery },
    responses: {
      302: { description: 'Weiterleitung', headers: RedirectHeaders },
      ...errorResponses([400, 429, 500, 503]),
    },
  },
);

export const logout = defineRoute(
  { ...WriteMeta, name: 'logout' },
  {
    method: 'post',
    path: '/api/v1/auth/logout',
    summary: 'Abmelden',
    responses: {
      204: { description: 'Abgemeldet' },
      ...errorResponses([400, 401, 403, 409, 429, 500]),
    },
  },
);

// ------------------------------------------------------------------------------------------------
// Sitzung und Netzwerk
// ------------------------------------------------------------------------------------------------

export const getMe = defineRoute(
  { ...ReadMeta, name: 'getMe' },
  {
    method: 'get',
    path: '/api/v1/me',
    summary: 'Angemeldeter Benutzer, Modus und CSRF-Token',
    responses: { 200: ok('Benutzer', Me), ...errorResponses([400, 401, 403, 429, 500]) },
  },
);

export const setSessionMode = defineRoute(
  { ...WriteMeta, name: 'setSessionMode' },
  {
    method: 'put',
    path: '/api/v1/session/mode',
    summary: 'Zwischen Live- und Demo-Modus wechseln',
    description:
      'Ohne Demo-Datenbank ist der Wechsel zu `demo` nicht möglich (503, `demo_unavailable`).',
    request: { body: requiredBody(SessionModeBody) },
    responses: {
      200: ok('Benutzer mit neuem Modus', Me),
      ...errorResponses([400, 401, 403, 409, 413, 429, 500, 503]),
    },
  },
);

export const getNetworkSummary = defineRoute(
  { ...ReadMeta, name: 'getNetworkSummary' },
  {
    method: 'get',
    path: '/api/v1/network/summary',
    summary: 'Kennzahlen, Zeitreihen und Verteilungen des Netzwerks',
    description:
      'Mit `audienceId` nur über die Mitglieder dieser Zielgruppe (Zeitreihen bleiben gesamt); eine fremde oder unbekannte Zielgruppe ergibt 404.',
    request: { query: NetworkSummaryQuery },
    responses: {
      200: ok('Netzwerk-Übersicht', NetworkSummary),
      ...errorResponses([400, 401, 403, 404, 429, 500]),
    },
  },
);

// ------------------------------------------------------------------------------------------------
// Personen
// ------------------------------------------------------------------------------------------------

export const listPersons = defineRoute(
  { ...ReadMeta, name: 'listPersons' },
  {
    method: 'get',
    path: '/api/v1/persons',
    summary: 'Menschen suchen und filtern',
    description: 'Eingeschränkte und gelöschte Personen erscheinen nicht.',
    request: { query: ListPersonsQuery },
    responses: {
      200: ok('Seite von Menschen', pageOf(PersonListItem)),
      ...errorResponses([400, 401, 403, 429, 500]),
    },
  },
);

export const getPerson = defineRoute(
  { ...ReadMeta, name: 'getPerson' },
  {
    method: 'get',
    path: '/api/v1/persons/{personId}',
    summary: 'Person mit Beziehung, Stärke-Faktoren und Identitäten',
    description:
      'Nur hier erscheinen eingeschränkte Personen (`restricted: true`, ohne Stärke und Verlauf).',
    request: { params: PersonParam },
    responses: {
      200: ok('Person', PersonDetail),
      ...errorResponses([400, 401, 403, 404, 429, 500]),
    },
  },
);

export const getPersonTimeline = defineRoute(
  { ...ReadMeta, name: 'getPersonTimeline' },
  {
    method: 'get',
    path: '/api/v1/persons/{personId}/timeline',
    summary: 'Verlauf einer Person',
    request: { params: PersonParam, query: PageQuery },
    responses: {
      200: ok('Seite des Verlaufs', pageOf(TimelineItem)),
      ...errorResponses([400, 401, 403, 404, 429, 500]),
    },
  },
);

export const addStrengthFeedback = defineRoute(
  { ...KeyMeta, name: 'addStrengthFeedback' },
  {
    method: 'post',
    path: '/api/v1/persons/{personId}/strength-feedback',
    summary: 'Rückmeldung „Beziehungsstärke stimmt nicht“ vormerken',
    request: {
      params: PersonParam,
      headers: IdempotencyKeyHeader,
      body: requiredBody(StrengthFeedbackBody),
    },
    responses: {
      204: { description: 'Vorgemerkt' },
      ...errorResponses([400, 401, 403, 404, 409, 413, 429, 500]),
    },
  },
);

export const mergePerson = defineRoute(
  { ...WriteMeta, ...ForbiddenInDemo, ...Steward, name: 'mergePerson' },
  {
    method: 'post',
    path: '/api/v1/persons/{personId}/merge',
    summary: 'Zwei Personen zusammenführen',
    description: 'Nur Datenverantwortliche; im Demo-Modus nicht möglich.',
    request: { params: PersonParam, body: requiredBody(MergePersonBody) },
    responses: {
      200: ok('Zielperson', PersonDetail),
      ...errorResponses([400, 401, 403, 404, 409, 413, 422, 429, 500]),
    },
  },
);

export const restrictPerson = defineRoute(
  { ...WriteMeta, ...Steward, name: 'restrictPerson' },
  {
    method: 'post',
    path: '/api/v1/persons/{personId}/restrict',
    summary: 'Verarbeitung einer Person einschränken (Art. 18)',
    description: 'Nur Datenverantwortliche.',
    request: { params: PersonParam, body: requiredBody(RestrictPersonBody) },
    responses: {
      204: { description: 'Eingeschränkt' },
      ...errorResponses([400, 401, 403, 404, 409, 413, 429, 500]),
    },
  },
);

export const unrestrictPerson = defineRoute(
  { ...WriteMeta, ...Steward, name: 'unrestrictPerson' },
  {
    method: 'delete',
    path: '/api/v1/persons/{personId}/restrict',
    summary: 'Einschränkung einer Person aufheben',
    description: 'Nur Datenverantwortliche.',
    request: { params: PersonParam },
    responses: {
      204: { description: 'Aufgehoben' },
      ...errorResponses([400, 401, 403, 404, 409, 429, 500]),
    },
  },
);

export const erasePerson = defineRoute(
  { ...KeyMeta, ...ForbiddenInDemo, ...Steward, name: 'erasePerson' },
  {
    method: 'post',
    path: '/api/v1/persons/{personId}/erase',
    summary: 'Person löschen (Betroffenenprozess)',
    description:
      'Nur Datenverantwortliche, nur Live-Sitzung. Genau eins von `privacyRequestId` und `reason`: bei `reason` legt der Server zuerst eine Anfrage `erasure` an.',
    request: {
      params: PersonParam,
      headers: IdempotencyKeyHeader,
      body: requiredBody(ErasePersonBody),
    },
    responses: {
      202: ok('Löschung gestartet', OperationRef),
      ...errorResponses([400, 401, 403, 404, 409, 413, 429, 500]),
    },
  },
);

export const exportPerson = defineRoute(
  { ...ReadMeta, ...Steward, name: 'exportPerson' },
  {
    method: 'get',
    path: '/api/v1/persons/{personId}/export',
    summary: 'Auskunft nach Art. 15 als JSON-Download',
    description:
      'Nur Datenverantwortliche (mandantenweite Auskunft, Person „verwaltbar“); gestreamt, höchstens 50 MB.',
    request: { params: PersonParam },
    responses: {
      200: {
        description: 'Auskunft als JSON-Dokument',
        headers: z.object({ 'content-disposition': z.string().max(200) }),
        content: json(PersonExport),
      },
      ...errorResponses([400, 401, 403, 404, 429, 500]),
    },
  },
);

// ------------------------------------------------------------------------------------------------
// Gespräche und Zuordnungen
// ------------------------------------------------------------------------------------------------

export const listConversations = defineRoute(
  { ...ReadMeta, name: 'listConversations' },
  {
    method: 'get',
    path: '/api/v1/conversations',
    summary: 'Gespräche, optional einer Person',
    request: { query: ListConversationsQuery },
    responses: {
      200: ok('Seite von Gesprächen', pageOf(ConversationListItem)),
      ...errorResponses([400, 401, 403, 404, 429, 500]),
    },
  },
);

export const listMessages = defineRoute(
  { ...ReadMeta, name: 'listMessages' },
  {
    method: 'get',
    path: '/api/v1/conversations/{conversationId}/messages',
    summary: 'Nachrichten eines Gesprächs (rückwärts paginiert)',
    request: { params: ConversationParam, query: ListMessagesQuery },
    responses: {
      200: ok('Seite von Nachrichten', pageOf(MessageItem)),
      ...errorResponses([400, 401, 403, 404, 429, 500]),
    },
  },
);

export const listIdentityReviews = defineRoute(
  { ...ReadMeta, name: 'listIdentityReviews' },
  {
    method: 'get',
    path: '/api/v1/identity-reviews',
    summary: 'Offene vermutete Zuordnungen',
    request: { query: PageQuery },
    responses: {
      200: ok('Seite von Zuordnungen', pageOf(IdentityReview)),
      ...errorResponses([400, 401, 403, 429, 500]),
    },
  },
);

export const decideIdentityReview = defineRoute(
  { ...KeyMeta, name: 'decideIdentityReview' },
  {
    method: 'post',
    path: '/api/v1/identity-reviews/{linkId}/decision',
    summary: 'Vermutete Zuordnung bestätigen oder ablehnen',
    request: {
      params: LinkParam,
      headers: IdempotencyKeyHeader,
      body: requiredBody(IdentityDecisionBody),
    },
    responses: {
      200: ok('Entscheidung', z.strictObject({ decisionId: z.uuid() })),
      ...errorResponses([400, 401, 403, 404, 409, 413, 429, 500]),
    },
  },
);

export const undoIdentityDecision = defineRoute(
  { ...WriteMeta, name: 'undoIdentityDecision' },
  {
    method: 'post',
    path: '/api/v1/identity-reviews/{linkId}/undo',
    summary: 'Eigene Zuordnungsentscheidung widerrufen',
    request: { params: LinkParam, body: requiredBody(UndoIdentityDecisionBody) },
    responses: {
      204: { description: 'Widerrufen' },
      ...errorResponses([400, 401, 403, 404, 409, 413, 429, 500]),
    },
  },
);

export const unlinkIdentity = defineRoute(
  { ...WriteMeta, name: 'unlinkIdentity' },
  {
    method: 'post',
    path: '/api/v1/identities/{identityId}/unlink',
    summary: 'Identität von der Person lösen („nicht dieselbe Person“)',
    request: { params: IdentityParam },
    responses: {
      204: { description: 'Gelöst' },
      ...errorResponses([400, 401, 403, 404, 409, 429, 500]),
    },
  },
);

export const prepareAction = defineRoute(
  { ...WriteMeta, name: 'prepareAction' },
  {
    method: 'post',
    path: '/api/v1/actions/prepare',
    summary: 'Link zum Öffnen von Profil oder Gespräch bei LinkedIn vorbereiten',
    description: 'Der Server erzeugt den Link; im Demo-Modus ist `url` leer.',
    request: { body: requiredBody(PrepareActionBody) },
    responses: {
      200: ok('Vorbereitete Aktion', PreparedAction),
      ...errorResponses([400, 401, 403, 404, 409, 413, 429, 500]),
    },
  },
);

// ------------------------------------------------------------------------------------------------
// Konten und Abrufe
// ------------------------------------------------------------------------------------------------

export const listAccounts = defineRoute(
  { ...ReadMeta, name: 'listAccounts' },
  {
    method: 'get',
    path: '/api/v1/accounts',
    summary: 'Verbundene Konten mit Zustand',
    request: { query: PageQuery },
    responses: {
      200: ok('Seite von Konten', pageOf(AccountStatus)),
      ...errorResponses([400, 401, 403, 429, 500]),
    },
  },
);

export const connectLinkedIn = defineRoute(
  { ...KeyMeta, ...ForbiddenInDemo, name: 'connectLinkedIn' },
  {
    method: 'post',
    path: '/api/v1/accounts/linkedin',
    summary: 'LinkedIn-Konto verbinden',
    description:
      'Rumpf höchstens 8 KB. Das Token wird nie geloggt und nie zurückgegeben; den Zustand liefert `listAccounts`.',
    request: { headers: IdempotencyKeyHeader, body: requiredBody(ConnectLinkedIn) },
    responses: {
      202: ok('Konto angelegt', z.strictObject({ accountId: z.uuid() })),
      ...errorResponses([400, 401, 403, 409, 413, 422, 429, 500, 503]),
    },
  },
);

export const renewAccountToken = defineRoute(
  { ...WriteMeta, ...ForbiddenInDemo, name: 'renewAccountToken' },
  {
    method: 'put',
    path: '/api/v1/accounts/{accountId}/token',
    summary: 'Zugriffstoken eines Kontos erneuern',
    description: 'Rumpf höchstens 8 KB. Das Token wird nie geloggt und nie zurückgegeben.',
    request: { params: AccountParam, body: requiredBody(RenewTokenBody) },
    responses: {
      200: ok('Konto', AccountStatus),
      ...errorResponses([400, 401, 403, 404, 409, 413, 422, 429, 500, 503]),
    },
  },
);

export const acknowledgePrivacy = defineRoute(
  { ...WriteMeta, ...ForbiddenInDemo, name: 'acknowledgePrivacy' },
  {
    method: 'post',
    path: '/api/v1/accounts/{accountId}/privacy-ack',
    summary: 'Datenschutzhinweis zum Konto bestätigen',
    request: { params: AccountParam },
    responses: {
      204: { description: 'Bestätigt' },
      ...errorResponses([400, 401, 403, 404, 409, 429, 500]),
    },
  },
);

export const refreshAccount = defineRoute(
  { ...KeyMeta, ...ForbiddenInDemo, name: 'refreshAccount' },
  {
    method: 'post',
    path: '/api/v1/accounts/{accountId}/refresh',
    summary: 'Konto jetzt aktualisieren',
    description: 'Höchstens ein nicht beendeter Auftrag je Konto; sechs Aufträge je Stunde.',
    request: {
      params: AccountParam,
      headers: IdempotencyKeyHeader,
      body: optionalBody(RefreshAccountBody),
    },
    responses: {
      202: ok('Auftrag gestartet', OperationRef),
      ...errorResponses([400, 401, 403, 404, 409, 413, 429, 500]),
    },
  },
);

export const pauseAccount = defineRoute(
  { ...WriteMeta, ...ForbiddenInDemo, name: 'pauseAccount' },
  {
    method: 'post',
    path: '/api/v1/accounts/{accountId}/pause',
    summary: 'Abrufe eines Kontos pausieren',
    request: { params: AccountParam },
    responses: {
      200: ok('Konto', AccountStatus),
      ...errorResponses([400, 401, 403, 404, 409, 429, 500]),
    },
  },
);

export const resumeAccount = defineRoute(
  { ...WriteMeta, ...ForbiddenInDemo, name: 'resumeAccount' },
  {
    method: 'post',
    path: '/api/v1/accounts/{accountId}/resume',
    summary: 'Abrufe eines Kontos fortsetzen',
    request: { params: AccountParam },
    responses: {
      200: ok('Konto', AccountStatus),
      ...errorResponses([400, 401, 403, 404, 409, 429, 500]),
    },
  },
);

export const disconnectAccount = defineRoute(
  { ...WriteMeta, ...ForbiddenInDemo, name: 'disconnectAccount' },
  {
    method: 'delete',
    path: '/api/v1/accounts/{accountId}',
    summary: 'Konto trennen',
    description: '`deleteData=true` löscht zusätzlich die abgerufenen Daten.',
    request: { params: AccountParam, query: DisconnectQuery },
    responses: {
      202: ok('Trennung gestartet', OperationRef),
      ...errorResponses([400, 401, 403, 404, 409, 429, 500]),
    },
  },
);

export const listFetchRuns = defineRoute(
  { ...ReadMeta, name: 'listFetchRuns' },
  {
    method: 'get',
    path: '/api/v1/accounts/{accountId}/runs',
    summary: 'Abrufverlauf eines Kontos',
    request: { params: AccountParam, query: ListFetchRunsQuery },
    responses: {
      200: ok('Seite von Abrufen', pageOf(FetchRunItem)),
      ...errorResponses([400, 401, 403, 404, 429, 500]),
    },
  },
);

export const listDataGaps = defineRoute(
  { ...ReadMeta, name: 'listDataGaps' },
  {
    method: 'get',
    path: '/api/v1/accounts/{accountId}/gaps',
    summary: 'Datenlücken eines Kontos',
    request: { params: AccountParam, query: PageQuery },
    responses: {
      200: ok('Seite von Lücken', pageOf(DataGapItem)),
      ...errorResponses([400, 401, 403, 404, 429, 500]),
    },
  },
);

export const acknowledgeDataGap = defineRoute(
  { ...WriteMeta, name: 'acknowledgeDataGap' },
  {
    method: 'post',
    path: '/api/v1/gaps/{gapId}/acknowledge',
    summary: 'Datenlücke als gesehen markieren',
    request: { params: GapParam },
    responses: {
      204: { description: 'Quittiert' },
      ...errorResponses([400, 401, 403, 404, 409, 429, 500]),
    },
  },
);

export const getProcessingProgress = defineRoute(
  { ...ReadMeta, name: 'getProcessingProgress' },
  {
    method: 'get',
    path: '/api/v1/processing-progress',
    summary: 'Aufbereitungsstand der abgerufenen Daten',
    description:
      'Nur Zählungen des angemeldeten Benutzers: wie viele abgerufene Einträge schon zu Personen, Gesprächen und Nachrichten aufbereitet sind.',
    responses: {
      200: ok('Aufbereitungsstand', ProcessingProgress),
      ...errorResponses([400, 401, 403, 429, 500]),
    },
  },
);

export const getActivity = defineRoute(
  { ...ReadMeta, name: 'getActivity' },
  {
    method: 'get',
    path: '/api/v1/activity',
    summary: 'Was passiert gerade',
    description:
      'Nur Daten des angemeldeten Benutzers, nur Zähler ohne Personenbezug: laufende Vorgänge mit Stand, die letzten Ergebnisse (24 Stunden), Probleme (nicht eingerichtet, pausiert, Fehler der letzten 24 Stunden nach Fehlerklasse) und Zähler je Zielgruppe.',
    responses: {
      200: ok('Aktivität', Activity),
      ...errorResponses([400, 401, 403, 429, 500]),
    },
  },
);

// ------------------------------------------------------------------------------------------------
// Erinnerungen, Vorgänge, Einstellungen
// ------------------------------------------------------------------------------------------------

export const listReminders = defineRoute(
  { ...ReadMeta, name: 'listReminders' },
  {
    method: 'get',
    path: '/api/v1/reminders',
    summary: 'Erinnerungen',
    description: 'Sortierung: Stufe `critical`, `warning`, `info`, dann jüngste zuerst.',
    request: { query: PageQuery },
    responses: {
      200: ok('Seite von Erinnerungen', pageOf(Reminder)),
      ...errorResponses([400, 401, 403, 429, 500]),
    },
  },
);

export const dismissReminder = defineRoute(
  { ...WriteMeta, name: 'dismissReminder' },
  {
    method: 'post',
    path: '/api/v1/reminders/{reminderId}/dismiss',
    summary: 'Erinnerung ausblenden',
    request: { params: ReminderParam },
    responses: {
      204: { description: 'Ausgeblendet' },
      ...errorResponses([400, 401, 403, 404, 409, 429, 500]),
    },
  },
);

export const getOperation = defineRoute(
  { ...ReadMeta, name: 'getOperation' },
  {
    method: 'get',
    path: '/api/v1/operations/{operationId}',
    summary: 'Status eines gestarteten Vorgangs',
    description: 'Nur eigene Vorgänge, sonst 404.',
    request: { params: OperationParam },
    responses: {
      200: ok('Vorgang', BackgroundOperation),
      ...errorResponses([400, 401, 403, 404, 429, 500]),
    },
  },
);

export const getSettings = defineRoute(
  { ...ReadMeta, name: 'getSettings' },
  {
    method: 'get',
    path: '/api/v1/settings',
    summary: 'Einstellungen lesen',
    responses: { 200: ok('Einstellungen', Settings), ...errorResponses([400, 401, 403, 429, 500]) },
  },
);

export const updateSettings = defineRoute(
  { ...WriteMeta, name: 'updateSettings' },
  {
    method: 'put',
    path: '/api/v1/settings',
    summary: 'Einstellungen ändern',
    description: 'Letzte Schreibung gewinnt; Änderungen werden protokolliert.',
    request: { body: requiredBody(SettingsUpdate) },
    responses: {
      200: ok('Einstellungen', Settings),
      ...errorResponses([400, 401, 403, 409, 413, 429, 500]),
    },
  },
);

export const resetDemo = defineRoute(
  { ...KeyMeta, name: 'resetDemo' },
  {
    method: 'post',
    path: '/api/v1/demo/reset',
    summary: 'Beispieldaten neu einspielen',
    description:
      'Nur in der Demo-Sitzung: eine Live-Sitzung erhält 403 `forbidden` mit `reason = demo_only` (Handlerregel, kein Routenattribut).',
    request: { headers: IdempotencyKeyHeader },
    responses: {
      202: ok('Zurücksetzen gestartet', OperationRef),
      ...errorResponses([400, 401, 403, 409, 429, 500]),
    },
  },
);

// ------------------------------------------------------------------------------------------------
// Betroffenenanfragen
// ------------------------------------------------------------------------------------------------

export const listPrivacyRequests = defineRoute(
  { ...ReadMeta, name: 'listPrivacyRequests' },
  {
    method: 'get',
    path: '/api/v1/privacy/requests',
    summary: 'Betroffenenanfragen',
    request: { query: ListPrivacyRequestsQuery },
    responses: {
      200: ok('Seite von Anfragen', pageOf(PrivacyRequest)),
      ...errorResponses([400, 401, 403, 429, 500]),
    },
  },
);

export const createPrivacyRequest = defineRoute(
  { ...KeyMeta, ...ForbiddenInDemo, name: 'createPrivacyRequest' },
  {
    method: 'post',
    path: '/api/v1/privacy/requests',
    summary: 'Betroffenenanfrage erfassen',
    request: { headers: IdempotencyKeyHeader, body: requiredBody(PrivacyRequestCreate) },
    responses: {
      201: ok('Angelegt', z.strictObject({ requestId: z.uuid() })),
      ...errorResponses([400, 401, 403, 404, 409, 413, 429, 500]),
    },
  },
);

export const updatePrivacyRequest = defineRoute(
  { ...WriteMeta, ...ForbiddenInDemo, name: 'updatePrivacyRequest' },
  {
    method: 'patch',
    path: '/api/v1/privacy/requests/{requestId}',
    summary: 'Betroffenenanfrage bearbeiten',
    description: 'Optimistische Sperre über `expectedUpdatedAt`.',
    request: { params: RequestParam, body: requiredBody(PrivacyRequestPatch) },
    responses: {
      200: ok('Anfrage', PrivacyRequest),
      ...errorResponses([400, 401, 403, 404, 409, 413, 429, 500]),
    },
  },
);

/** Alle Routen des Moduls in der Reihenfolge von Plan D 3.4. */
export const coreRoutes = [
  healthz,
  readyz,
  login,
  authCallback,
  logout,
  getMe,
  setSessionMode,
  getNetworkSummary,
  listPersons,
  getPerson,
  getPersonTimeline,
  addStrengthFeedback,
  mergePerson,
  restrictPerson,
  unrestrictPerson,
  erasePerson,
  exportPerson,
  listConversations,
  listMessages,
  listIdentityReviews,
  decideIdentityReview,
  undoIdentityDecision,
  unlinkIdentity,
  prepareAction,
  listAccounts,
  connectLinkedIn,
  renewAccountToken,
  acknowledgePrivacy,
  refreshAccount,
  pauseAccount,
  resumeAccount,
  disconnectAccount,
  listFetchRuns,
  listDataGaps,
  acknowledgeDataGap,
  getProcessingProgress,
  getActivity,
  listReminders,
  dismissReminder,
  getOperation,
  getSettings,
  updateSettings,
  resetDemo,
  listPrivacyRequests,
  createPrivacyRequest,
  updatePrivacyRequest,
] as const satisfies readonly ApiRoute[];

/** Gründe (`reason`) der Problem-Antworten dieses Moduls; Dashboard und Handler nutzen dieselbe Liste (RW-F4). */
export { CoreProblemReason } from './core-schemas.js';
